<template>
  <!--
    DsLink — text link del DS (elements/link/link.css). Gemelo del ghost button:
    Geist sans 500, normal case, sin fill/border. Hover → accent + arrow translate.
    `inline` para links embebidos en prosa. href → <a>, to → <router-link>, else <button>.
  -->
  <component
    :is="tag"
    class="ds-link"
    :class="{ 'ds-link--inline': inline, 'has-trailing': hasTrailing }"
    v-bind="tagAttrs"
  >
    <slot />
    <span v-if="hasTrailing" class="ds-link__trailing"><slot name="trailing" /></span>
  </component>
</template>

<script>
export default {
  name: 'DsLink',
  props: {
    href: { type: String, default: null },
    to: { type: [String, Object], default: null },
    inline: { type: Boolean, default: false },
  },
  computed: {
    tag() {
      if (this.to) return 'router-link';
      if (this.href) return 'a';
      return 'button';
    },
    hasTrailing() {
      return !!this.$slots.trailing && !this.inline;
    },
    /**
     * Solo los atributos que le tocan a ESTE tag. Antes se bindeaban los tres
     * con `undefined` para los que no aplicaban, y sobre un `router-link` ese
     * `:href="undefined"` explícito le PISABA el href que el propio componente
     * calcula: el `<a>` salía sin destino. Seguía navegando al click, pero no
     * se podía abrir en pestaña nueva ni el lector de pantalla lo anunciaba
     * como link.
     */
    tagAttrs() {
      if (this.tag === 'a') return { href: this.href };
      if (this.tag === 'router-link') return { to: this.to };
      return { type: 'button' };
    },
  },
};
</script>

<style scoped lang="scss">
.ds-link {
  font-family: var(--font-sans);
  font-size: var(--text-body-s-size);
  font-weight: 500;
  color: var(--text-default);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
  cursor: pointer;
  background: none;
  border: 0;
  padding: 0;
  transition: color var(--duration-base, 240ms) var(--ease-default, ease);
  outline: none;
}
.ds-link.has-trailing { justify-content: space-between; }
.ds-link__trailing {
  display: inline-flex;
  align-items: center;
  transition: transform var(--duration-base, 240ms) var(--ease-default, ease);
  /* Icono a 16px — DIVERGENCIA DELIBERADA de foundation (link.css usa 20px). En la plataforma
     (UI denso, texto 14px) los links conviven con botones (icono 16px) y filas (chevron 16px);
     un icono de link a 20px rompe el ritmo. 16px = una sola escala de icono en toda la app.
     NO es solo preferencia de plataforma: el 20 vs 16 es DRIFT de foundation (mismo problema en
     web y academy, links a 20 y a 16). Fix real = tokenizar escala de iconos en foundation.
     Ver docs/migration-ledger.md §5 {#foundation-drift}. Cuando exista el token, referenciarlo acá. */
  :deep(svg) { width: 16px; height: 16px; }
}

@media (hover: hover) and (pointer: fine) {
  .ds-link:hover { color: var(--text-accent); }
  .ds-link:hover .ds-link__trailing { transform: translateX(var(--hover-arrow-shift, 4px)); }
}
.ds-link:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 3px;
  border-radius: var(--radius-sm);
}
.ds-link:active {
  color: var(--accent-hover);
  transition-duration: var(--duration-tap, 80ms);
}

/* Inline (dentro de prosa) — hereda tamaño/peso, color accent directo. */
.ds-link--inline {
  display: inline;
  font-size: inherit;
  font-weight: inherit;
  color: var(--text-accent);   /* accent como TEXTO — teal-300 en slate (AA), no el teal-500 de fill */
  gap: 0;
}
@media (hover: hover) and (pointer: fine) {
  .ds-link--inline:hover { color: var(--accent-hover); }
}
.ds-link--inline:active { color: var(--accent-pressed); }
</style>

<!--
  Bloque NO-scoped: overrides que dependen del ANCESTRO [data-surface]. El scoped NO puede
  targetear un ancestro externo — `:deep([data-surface]) .ds-link` compila a
  `[data-v] [data-surface] .ds-link`, que exige un [data-v] del componente ARRIBA de la card
  (no existe) → nunca matchea. Mismo patrón que DsButton. El color base sobre oscuro ya lo da
  el cascade de --text-default (vellum en immersive), así que acá solo arreglamos el hover.
-->
<style lang="scss">
/* Sobre superficie oscura (immersive/slate/chrome/contour) el link NO cambia de color en hover:
   se queda en --text-default (= vellum), y el único affordance es el arrow-translate. Es la
   decisión de foundation (link.css: "sobre slate no hay color hover"). El --accent sobre oscuro
   (teal-500) quedaba oscuro/low-contrast → era el "hover oscuro" del card de Training.
   Doble .ds-link gana especificidad al hover scoped (que va a --accent en claro). */
[data-surface="immersive"] .ds-link.ds-link:hover,
[data-surface="slate"] .ds-link.ds-link:hover,
[data-surface="chrome"] .ds-link.ds-link:hover,
[data-surface="contour"] .ds-link.ds-link:hover {
  color: var(--text-default);
}
</style>
